import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';
import { getAuthToken } from './authUtils';

/**
 * Create axios instance with default config
 */
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request interceptor to add auth token
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle errors
 * This will be enhanced by NotificationContext for UI notifications
 */
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('[Axios] Network error:', error.message);
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        isNetworkError: true,
        originalError: error
      });
    }

    // Handle HTTP errors with standardized format
    const { status, data } = error.response;

    // Log error for debugging
    console.error(`[Axios] HTTP ${status} error:`, data);

    // For 401/403, redirect to login (handled by individual services)
    if (status === 401 || status === 403) {
      // Check if it's actually an auth error vs business logic error (like CHAT_LOCKED)
      const isAuthError = !data?.error?.code ||
        ['AUTHENTICATION_ERROR', 'AUTHORIZATION_ERROR', 'INVALID_TOKEN', 'TOKEN_EXPIRED'].includes(data?.error?.code);

      if (isAuthError) {
        console.log('[Axios] Authentication error detected, clearing token');
        localStorage.removeItem('token');
        // Individual services will handle redirect to login
      }
    }

    // Return the error in a consistent format
    return Promise.reject(error);
  }
);

/**
 * Helper to setup notification interceptor
 * This should be called from a component that has access to NotificationContext
 * @param {Function} showError - The showError function from NotificationContext
 */
export const setupNotificationInterceptor = (showError) => {
  // Remove any existing notification interceptor
  if (axiosInstance.interceptors.response.handlers.length > 1) {
    axiosInstance.interceptors.response.eject(axiosInstance.interceptors.response.handlers.length - 1);
  }

  // Add notification interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Don't show notification for network errors (offline queue handles this)
      if (error.isNetworkError) {
        return Promise.reject(error);
      }

      // Don't show notification for certain error codes (handled by components)
      const suppressedCodes = [
        'CHAT_LOCKED',
        'AUTHENTICATION_ERROR',
        'AUTHORIZATION_ERROR',
        'INVALID_TOKEN',
        'TOKEN_EXPIRED'
      ];
      const errorCode = error.response?.data?.error?.code;

      // Also suppress 401/403 errors (authentication/authorization)
      const status = error.response?.status;
      const isAuthError = status === 401 || status === 403;

      if (!suppressedCodes.includes(errorCode) && !isAuthError) {
        // Show notification for other errors
        showError(error);
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;
